import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Settings, Link2, Banknote, FileText, RefreshCw, Plus, Trash2, Send, Download, Upload, Copy, Check, X } from 'lucide-react';
import { theme, cardStyle, inputStyle, labelStyle, tdStyle, thStyle, btnStyle, statCard } from '../theme';

const fmtINR = n => `₹${(Number(n)||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const todayISO = () => new Date().toISOString().slice(0,10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate()-n); return d.toISOString().slice(0,10); };

export default function IntegrationsHubPage({ API_BASE, currentUser, patientsList }) {
    const [tab, setTab] = useState('configs');

    const tabBtn = (k, Icon, label) => (
        <button onClick={()=>setTab(k)} style={{padding:'9px 14px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.82rem',
            backgroundColor: tab===k ? theme.primary : '#e9ecef', color: tab===k ? 'white' : '#555',
            display:'inline-flex', alignItems:'center', gap:'6px'}}>
            <Icon size={13}/>{label}
        </button>
    );

    return (
        <div>
            <div style={{marginBottom:'16px'}}>
                <h2 style={{margin:0, color:theme.navy, fontSize:'1.4rem', fontWeight:'700'}}>Integrations Hub</h2>
                <p style={{margin:'2px 0 0', color:theme.textMuted, fontSize:'0.85rem'}}>Payment gateways · Bank reconciliation · Tally · QuickBooks</p>
            </div>
            <div style={{display:'flex', gap:'6px', marginBottom:'18px', flexWrap:'wrap'}}>
                {tabBtn('configs',  Settings,    'Provider Configs')}
                {tabBtn('links',    Link2,       'Payment Links')}
                {tabBtn('bank',     Banknote,    'Bank Reconciliation')}
                {tabBtn('tally',    FileText,    'Tally Export')}
                {tabBtn('qb',       FileText,    'QuickBooks')}
            </div>

            {tab==='configs' && <ConfigsTab API_BASE={API_BASE}/>}
            {tab==='links'   && <LinksTab   API_BASE={API_BASE} currentUser={currentUser} patientsList={patientsList}/>}
            {tab==='bank'    && <BankTab    API_BASE={API_BASE} currentUser={currentUser}/>}
            {tab==='tally'   && <TallyTab   API_BASE={API_BASE}/>}
            {tab==='qb'      && <QbTab      API_BASE={API_BASE}/>}
        </div>
    );
}

// ── Configs ──────────────────────────────────────────────────────────
function ConfigsTab({ API_BASE }) {
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);

    const refresh = useCallback(async () => { try { const r = await axios.get(`${API_BASE}/integrations/configs`); setList(r.data); } catch {} }, [API_BASE]);
    useEffect(()=>{refresh();}, [refresh]);

    const KNOWN = ['razorpay','payu','stripe','pinelabs','quickbooks','tally','msg91','2factor-otp'];
    const known = list.map(c => c.provider);
    const missing = KNOWN.filter(p => !known.includes(p));

    const empty = (provider) => ({ provider, mode:'sandbox', enabled:false, credentials:{}, syncFrequency:'manual' });
    const save = async () => {
        try {
            await axios.put(`${API_BASE}/integrations/configs/${editing.provider}`, editing);
            setEditing(null); refresh();
        } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };
    const del = async (p) => {
        if (!window.confirm(`Delete ${p} config?`)) return;
        await axios.delete(`${API_BASE}/integrations/configs/${p}`); refresh();
    };

    const credKeys = (p) => ({
        razorpay:     ['keyId','keySecret','webhookSecret'],
        payu:         ['merchantKey','merchantSalt'],
        stripe:       ['publishableKey','secretKey','webhookSecret'],
        pinelabs:     ['merchantId','storeId','terminalId','apiKey'],
        quickbooks:   ['clientId','clientSecret'],
        tally:        ['serverUrl','companyName'],
        msg91:        ['authKey','senderId','templateId'],
        '2factor-otp':['apiKey','route','templateName'],
    }[p] || ['apiKey','apiSecret']);

    const testConn = async () => {
        try {
            await axios.put(`${API_BASE}/integrations/configs/${editing.provider}`, editing);
            const r = await axios.post(`${API_BASE}/integrations/configs/${editing.provider}/test`, {});
            if (r.data?.ok) {
                alert(`✓ ${r.data.message || 'OK'}${r.data.balance ? `\nBalance: ${r.data.balance}` : ''}${r.data.route ? `\nRoute: ${r.data.route}` : ''}`);
            } else {
                alert(`✗ ${r.data?.message || 'Test failed'}`);
            }
            refresh();
        } catch (e) { alert(e.response?.data?.error || 'Test failed'); }
    };

    return (
        <div>
            <div style={{display:'flex', gap:'8px', marginBottom:'14px', flexWrap:'wrap'}}>
                {missing.map(p => (
                    <button key={p} onClick={()=>setEditing(empty(p))} style={{padding:'7px 14px', backgroundColor:theme.primary+'22', color:theme.primary, border:`1px dashed ${theme.primary}`, borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'0.82rem'}}>
                        + {p}
                    </button>
                ))}
            </div>

            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))', gap:'12px'}}>
                {list.map(c => (
                    <div key={c.provider} style={{...cardStyle, padding:'14px', borderLeft:`4px solid ${c.enabled?theme.success:theme.textMuted}`}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px'}}>
                            <div>
                                <strong style={{color:theme.navy, fontSize:'1rem', textTransform:'uppercase'}}>{c.provider}</strong>
                                <div style={{fontSize:'0.74rem', color:theme.textMuted}}>{c.label || ''}</div>
                            </div>
                            <div style={{display:'flex', gap:'4px'}}>
                                <span style={{padding:'2px 10px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:'700', backgroundColor: c.mode==='live'?theme.warning+'33':theme.info+'33', color: c.mode==='live'?theme.warning:theme.info}}>{c.mode}</span>
                                <span style={{padding:'2px 10px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:'700', backgroundColor: c.enabled?theme.success+'33':theme.textMuted+'33', color: c.enabled?theme.success:theme.textMuted}}>{c.enabled?'ENABLED':'OFF'}</span>
                            </div>
                        </div>
                        {c.lastSyncAt && <div style={{fontSize:'0.74rem', color:theme.textMuted, marginBottom:'4px'}}>Last sync: {fmtDate(c.lastSyncAt)} {c.lastSyncOk ? <Check size={11} color={theme.success} style={{verticalAlign:'middle'}}/> : <X size={11} color={theme.danger} style={{verticalAlign:'middle'}}/>}</div>}
                        {c.lastError && <div style={{fontSize:'0.74rem', color:theme.danger, marginBottom:'4px'}}>{c.lastError}</div>}
                        {Object.keys(c.credentials || {}).length > 0 && (
                            <div style={{fontSize:'0.72rem', color:theme.textMuted, fontFamily:'monospace', backgroundColor:'#f8fafc', padding:'6px 8px', borderRadius:'6px', marginTop:'6px'}}>
                                {Object.entries(c.credentials).map(([k,v]) => <div key={k}>{k}: {String(v||'(empty)')}</div>)}
                            </div>
                        )}
                        <div style={{display:'flex', gap:'6px', marginTop:'10px'}}>
                            <button onClick={()=>setEditing({...c, credentials: {...(c.credentials||{})}})} style={{flex:1, padding:'6px', backgroundColor:theme.info, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.78rem', fontWeight:'600'}}>Edit</button>
                            <button onClick={()=>del(c.provider)} style={{padding:'6px 10px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.78rem'}}><Trash2 size={12}/></button>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <Modal onClose={()=>setEditing(null)} title={`Configure ${editing.provider.toUpperCase()}`}>
                    <div style={{display:'flex', gap:'10px', marginBottom:'12px'}}>
                        <div style={{flex:1}}>
                            <label style={labelStyle}>Mode</label>
                            <select value={editing.mode} onChange={e=>setEditing({...editing, mode:e.target.value})} style={inputStyle}>
                                <option value="sandbox">Sandbox / Test</option>
                                <option value="live">Live / Production</option>
                            </select>
                        </div>
                        <div style={{flex:1}}>
                            <label style={labelStyle}>Status</label>
                            <button onClick={()=>setEditing({...editing, enabled:!editing.enabled})} style={{...inputStyle, padding:'10px', textAlign:'left', cursor:'pointer', backgroundColor: editing.enabled?theme.success+'22':'white'}}>{editing.enabled ? '✓ Enabled' : 'Disabled'}</button>
                        </div>
                    </div>
                    <label style={labelStyle}>Display Label (optional)</label>
                    <input value={editing.label||''} onChange={e=>setEditing({...editing, label:e.target.value})} style={inputStyle}/>
                    <h4 style={{margin:'14px 0 6px', color:theme.navy, fontSize:'0.86rem'}}>Credentials</h4>
                    {credKeys(editing.provider).map(k => {
                        const isRouteField = editing.provider === '2factor-otp' && k === 'route';
                        return (
                            <div key={k} style={{marginBottom:'6px'}}>
                                <label style={labelStyle}>{k}</label>
                                {isRouteField ? (
                                    <select value={editing.credentials?.[k] || 'sms'} onChange={e=>setEditing({...editing, credentials:{...editing.credentials, [k]:e.target.value}})} style={inputStyle}>
                                        <option value="sms">SMS (needs DLT template)</option>
                                        <option value="voice">Voice call (no DLT)</option>
                                    </select>
                                ) : (
                                    <input value={editing.credentials?.[k]||''} onChange={e=>setEditing({...editing, credentials:{...editing.credentials, [k]:e.target.value}})} style={{...inputStyle, fontFamily:'monospace', fontSize:'0.84rem'}}/>
                                )}
                            </div>
                        );
                    })}
                    {editing.provider === '2factor-otp' && (
                        <div style={{fontSize:'0.78rem', color:theme.textMuted, marginTop:'4px', padding:'8px', backgroundColor:'#f8fafc', borderRadius:'6px', lineHeight:'1.5'}}>
                            Get your key from <strong>2factor.in</strong> → API. Paste it in <code>apiKey</code>, pick <code>sms</code> (standard, needs DLT template name below) or <code>voice</code> (phone call, no DLT). Leave <code>templateName</code> empty to use 2Factor's default AUTOGEN template.
                        </div>
                    )}
                    <div style={{display:'flex', gap:'8px', marginTop:'10px'}}>
                        <button onClick={save} style={{...btnStyle, marginTop:0, flex:1}}>Save</button>
                        <button onClick={testConn} style={{...btnStyle, marginTop:0, backgroundColor:theme.info}}>Save & Test</button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// ── Payment Links ────────────────────────────────────────────────────
function LinksTab({ API_BASE, currentUser, patientsList = [] }) {
    const [list, setList] = useState([]);
    const [creating, setCreating] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');

    const refresh = useCallback(async () => { try { const r = await axios.get(`${API_BASE}/integrations/links${statusFilter?`?status=${statusFilter}`:''}`); setList(r.data); } catch {} }, [API_BASE, statusFilter]);
    useEffect(()=>{refresh();}, [refresh]);

    const empty = () => ({ provider:'razorpay', amount:0, patientId:'', description:'', expiresInHours:72 });
    const save = async () => {
        try {
            await axios.post(`${API_BASE}/integrations/links`, { ...creating, createdBy: currentUser?.username });
            setCreating(null); refresh();
        } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };
    const sync = async (id) => { try { await axios.post(`${API_BASE}/integrations/links/${id}/sync`, {}); refresh(); } catch (e) { alert(e.response?.data?.error || 'Failed'); } };
    const copy = (url) => { navigator.clipboard.writeText(url); alert('Copied'); };

    const COL = { created:{bg:'#e5e7eb',c:'#374151'}, sent:{bg:'#dbeafe',c:'#1e40af'}, opened:{bg:'#fef3c7',c:'#92400e'}, paid:{bg:'#d1fae5',c:'#065f46'}, expired:{bg:'#e5e7eb',c:'#374151'}, cancelled:{bg:'#fee2e2',c:'#991b1b'}, failed:{bg:'#fee2e2',c:'#991b1b'} };

    return (
        <div>
            <div style={{display:'flex', justifyContent:'space-between', marginBottom:'12px'}}>
                <div style={{display:'flex', gap:'6px', flexWrap:'wrap'}}>
                    {['','created','sent','paid','expired','cancelled'].map(s => (
                        <button key={s} onClick={()=>setStatusFilter(s)} style={{padding:'5px 12px', borderRadius:'14px', border:`1px solid ${statusFilter===s?theme.primary:theme.border}`, cursor:'pointer', fontSize:'0.78rem', fontWeight:'600', backgroundColor:statusFilter===s?theme.primary:'white', color:statusFilter===s?'white':'#555'}}>{s||'All'}</button>
                    ))}
                </div>
                <button onClick={()=>setCreating(empty())} style={{...btnStyle, marginTop:0, padding:'8px 14px', fontSize:'0.85rem'}}><Plus size={14} style={{verticalAlign:'middle', marginRight:'4px'}}/>New Link</button>
            </div>
            <div style={cardStyle}>
                <table style={{width:'100%', borderCollapse:'collapse'}}>
                    <thead><tr>
                        <th style={thStyle}>Created</th><th style={thStyle}>Provider</th>
                        <th style={thStyle}>Customer</th><th style={thStyle}>Amount</th>
                        <th style={thStyle}>URL</th><th style={thStyle}>Status</th>
                        <th style={thStyle}>Paid At</th><th style={thStyle}></th>
                    </tr></thead>
                    <tbody>
                        {list.map(l => {
                            const col = COL[l.status]||{bg:'#f1f5f9',c:'#475569'};
                            return (
                                <tr key={l._id}>
                                    <td style={tdStyle}>{fmtDate(l.createdAt)}</td>
                                    <td style={tdStyle}><strong style={{textTransform:'uppercase'}}>{l.provider}</strong></td>
                                    <td style={tdStyle}>{l.customerName}<div style={{fontSize:'0.72rem', color:theme.textMuted}}>{l.customerPhone}</div></td>
                                    <td style={tdStyle}><strong>{fmtINR(l.amount)}</strong></td>
                                    <td style={tdStyle}>
                                        {l.shortUrl ? (
                                            <div style={{display:'flex', gap:'4px', alignItems:'center'}}>
                                                <a href={l.shortUrl} target="_blank" rel="noopener noreferrer" style={{color:theme.info, fontSize:'0.78rem'}}>open</a>
                                                <button onClick={()=>copy(l.shortUrl)} style={{background:'none', border:'none', cursor:'pointer'}}><Copy size={12}/></button>
                                            </div>
                                        ) : '—'}
                                    </td>
                                    <td style={tdStyle}><span style={{padding:'2px 8px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:'700', backgroundColor:col.bg, color:col.c}}>{l.status}</span></td>
                                    <td style={tdStyle}>{l.paidAt ? new Date(l.paidAt).toLocaleString('en-IN') : '—'}</td>
                                    <td style={tdStyle}>{l.provider==='razorpay' && l.status !== 'paid' && <button onClick={()=>sync(l._id)} title="Sync status" style={{padding:'4px 8px', backgroundColor:theme.primary, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem'}}><RefreshCw size={11}/></button>}</td>
                                </tr>
                            );
                        })}
                        {list.length===0 && <tr><td colSpan="8" style={{...tdStyle, textAlign:'center', color:theme.textMuted, padding:'30px'}}>No payment links yet</td></tr>}
                    </tbody>
                </table>
            </div>

            {creating && (
                <Modal onClose={()=>setCreating(null)} title="New Payment Link">
                    <label style={labelStyle}>Provider</label>
                    <select value={creating.provider} onChange={e=>setCreating({...creating, provider:e.target.value})} style={inputStyle}>
                        <option value="razorpay">Razorpay</option>
                        <option value="payu">PayU</option>
                    </select>
                    <label style={labelStyle}>Patient</label>
                    <select value={creating.patientId} onChange={e=>setCreating({...creating, patientId:e.target.value})} style={inputStyle}>
                        <option value="">Walk-in / no patient</option>
                        {patientsList.map(p => <option key={p._id} value={p._id}>{p.name} · {p.phone}</option>)}
                    </select>
                    <label style={labelStyle}>Amount (₹)</label>
                    <input type="number" value={creating.amount} onChange={e=>setCreating({...creating, amount:Number(e.target.value)||0})} style={inputStyle}/>
                    <label style={labelStyle}>Description</label>
                    <input value={creating.description} onChange={e=>setCreating({...creating, description:e.target.value})} style={inputStyle}/>
                    <label style={labelStyle}>Expires in (hours)</label>
                    <input type="number" value={creating.expiresInHours} onChange={e=>setCreating({...creating, expiresInHours:Number(e.target.value)||72})} style={inputStyle}/>
                    <button onClick={save} style={{...btnStyle, marginTop:'10px'}}>Generate Link</button>
                </Modal>
            )}
        </div>
    );
}

// ── Bank Reconciliation ──────────────────────────────────────────────
function BankTab({ API_BASE, currentUser }) {
    const [list, setList] = useState([]);
    const [selected, setSelected] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importForm, setImportForm] = useState({ bankName:'', accountNo:'', accountAlias:'', csv:'' });

    const refresh = useCallback(async () => { try { const r = await axios.get(`${API_BASE}/integrations/bank-statements`); setList(r.data); } catch {} }, [API_BASE]);
    useEffect(()=>{refresh();}, [refresh]);

    const doImport = async () => {
        if (!importForm.csv) return alert('Paste CSV content');
        try {
            const r = await axios.post(`${API_BASE}/integrations/bank-statements/import`, { ...importForm, importedBy: currentUser?.username });
            setImporting(false); refresh();
            alert(`Imported ${r.data.lines.length} lines`);
        } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };
    const autoMatch = async (id) => {
        const r = await axios.post(`${API_BASE}/integrations/bank-statements/${id}/auto-match`, {});
        alert(`Matched: ${r.data.matchedExact} exact + ${r.data.matchedAmtDate} by amt/date · ${r.data.unmatched} unmatched`);
        refresh();
        if (selected) loadDetail(selected._id);
    };
    const loadDetail = async (id) => { const r = await axios.get(`${API_BASE}/integrations/bank-statements/${id}`); setSelected(r.data); };
    const ignoreLine = async (lid) => { await axios.put(`${API_BASE}/integrations/bank-statements/${selected._id}/lines/${lid}/ignore`, { user: currentUser?.username }); loadDetail(selected._id); };

    const lineCol = { matched:{bg:'#d1fae5',c:'#065f46'}, manual:{bg:'#dbeafe',c:'#1e40af'}, ignored:{bg:'#e5e7eb',c:'#374151'}, unmatched:{bg:'#fef3c7',c:'#92400e'} };

    return (
        <div>
            <div style={{display:'flex', justifyContent:'space-between', marginBottom:'12px'}}>
                <h3 style={{margin:0, color:theme.navy}}>Bank Statements ({list.length})</h3>
                <button onClick={()=>setImporting(true)} style={{...btnStyle, marginTop:0, padding:'8px 14px', fontSize:'0.85rem'}}><Upload size={14} style={{verticalAlign:'middle', marginRight:'4px'}}/>Import CSV</button>
            </div>

            {!selected && (
                <div style={cardStyle}>
                    <table style={{width:'100%', borderCollapse:'collapse'}}>
                        <thead><tr>
                            <th style={thStyle}>Bank</th><th style={thStyle}>Account</th>
                            <th style={thStyle}>Period</th><th style={thStyle}>Lines</th>
                            <th style={thStyle}>Total Credit</th><th style={thStyle}>Status</th><th style={thStyle}></th>
                        </tr></thead>
                        <tbody>
                            {list.map(s => (
                                <tr key={s._id}>
                                    <td style={tdStyle}><strong>{s.bankName||'—'}</strong></td>
                                    <td style={tdStyle}>{s.accountAlias||s.accountNo||'—'}</td>
                                    <td style={tdStyle}>{fmtDate(s.fromDate)} → {fmtDate(s.toDate)}</td>
                                    <td style={tdStyle}>{s.lines?.length||0}</td>
                                    <td style={tdStyle}>{fmtINR(s.totalCredit)}</td>
                                    <td style={tdStyle}><span style={{padding:'2px 10px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:'700', backgroundColor:'#dbeafe', color:'#1e40af'}}>{s.status}</span></td>
                                    <td style={tdStyle}><button onClick={()=>loadDetail(s._id)} style={{padding:'4px 12px', backgroundColor:theme.info, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem'}}>Open</button></td>
                                </tr>
                            ))}
                            {list.length===0 && <tr><td colSpan="7" style={{...tdStyle, textAlign:'center', color:theme.textMuted, padding:'30px'}}>No statements imported</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && (
                <div>
                    <button onClick={()=>setSelected(null)} style={{padding:'6px 14px', marginBottom:'10px', backgroundColor:'white', border:`1px solid ${theme.border}`, borderRadius:'8px', cursor:'pointer', fontSize:'0.82rem'}}>← Back</button>
                    <div style={{...cardStyle, marginBottom:'12px'}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                            <div>
                                <strong style={{color:theme.navy}}>{selected.bankName} · {selected.accountAlias||selected.accountNo}</strong>
                                <div style={{fontSize:'0.82rem', color:theme.textMuted}}>{fmtDate(selected.fromDate)} → {fmtDate(selected.toDate)} · {selected.lines.length} lines · Credit {fmtINR(selected.totalCredit)} · Debit {fmtINR(selected.totalDebit)}</div>
                            </div>
                            <button onClick={()=>autoMatch(selected._id)} style={{...btnStyle, marginTop:0, padding:'8px 16px', fontSize:'0.85rem'}}>Auto-match</button>
                        </div>
                    </div>
                    <div style={cardStyle}>
                        <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.84rem'}}>
                            <thead><tr>
                                <th style={thStyle}>Date</th><th style={thStyle}>Description</th><th style={thStyle}>Ref</th>
                                <th style={thStyle}>Debit</th><th style={thStyle}>Credit</th>
                                <th style={thStyle}>Status</th><th style={thStyle}></th>
                            </tr></thead>
                            <tbody>
                                {selected.lines.map(l => {
                                    const col = lineCol[l.matchStatus]||{};
                                    return (
                                        <tr key={l._id}>
                                            <td style={tdStyle}>{fmtDate(l.txnDate)}</td>
                                            <td style={tdStyle}>{l.description}</td>
                                            <td style={tdStyle}><code style={{fontSize:'0.72rem'}}>{l.referenceNo||'—'}</code></td>
                                            <td style={tdStyle}>{l.debit > 0 ? fmtINR(l.debit) : '—'}</td>
                                            <td style={tdStyle}>{l.credit > 0 ? <strong>{fmtINR(l.credit)}</strong> : '—'}</td>
                                            <td style={tdStyle}><span style={{padding:'2px 8px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:'700', backgroundColor:col.bg, color:col.c}}>{l.matchStatus}</span></td>
                                            <td style={tdStyle}>{l.matchStatus==='unmatched' && l.credit > 0 && <button onClick={()=>ignoreLine(l._id)} style={{padding:'3px 8px', backgroundColor:theme.textMuted, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.7rem'}}>Ignore</button>}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {importing && (
                <Modal onClose={()=>setImporting(false)} title="Import Bank Statement (CSV)" wide>
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', marginBottom:'10px'}}>
                        <div><label style={labelStyle}>Bank Name</label><input value={importForm.bankName} onChange={e=>setImportForm({...importForm, bankName:e.target.value})} style={inputStyle} placeholder="e.g. SBI"/></div>
                        <div><label style={labelStyle}>Account No</label><input value={importForm.accountNo} onChange={e=>setImportForm({...importForm, accountNo:e.target.value})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>Alias</label><input value={importForm.accountAlias} onChange={e=>setImportForm({...importForm, accountAlias:e.target.value})} style={inputStyle} placeholder="Main current"/></div>
                    </div>
                    <label style={labelStyle}>CSV content (paste from your bank export)</label>
                    <textarea rows={10} value={importForm.csv} onChange={e=>setImportForm({...importForm, csv:e.target.value})} style={{...inputStyle, fontFamily:'monospace', fontSize:'0.78rem'}} placeholder="Date,Description,Ref,Debit,Credit,Balance&#10;15/04/2026,UPI ROHIT SHARMA,UPI/123456,0,5000,12000"/>
                    <p style={{fontSize:'0.78rem', color:theme.textMuted, margin:'6px 0 0'}}>Headers detected: Date · Description / Narration · Ref / UTR / Cheque · Debit / Withdrawal · Credit / Deposit · Balance</p>
                    <button onClick={doImport} style={{...btnStyle, marginTop:'10px'}}>Import & Parse</button>
                </Modal>
            )}
        </div>
    );
}

// ── Tally Export ─────────────────────────────────────────────────────
function TallyTab({ API_BASE }) {
    const [from, setFrom] = useState(daysAgo(30));
    const [to, setTo] = useState(todayISO());

    const dlSales    = () => window.open(`${API_BASE}/integrations/tally/sales?from=${from}&to=${to}`, '_blank');
    const dlReceipts = () => window.open(`${API_BASE}/integrations/tally/receipts?from=${from}&to=${to}`, '_blank');

    return (
        <div>
            <div style={{...cardStyle, marginBottom:'14px'}}>
                <h3 style={{margin:'0 0 10px', color:theme.navy, fontSize:'1rem'}}>Tally XML Export</h3>
                <p style={{fontSize:'0.82rem', color:theme.textMuted, margin:'0 0 12px'}}>Generates Tally Prime / ERP9 import XML. Open in Tally → Gateway → Import Data → Vouchers → select the downloaded file.</p>
                <div style={{display:'flex', alignItems:'end', gap:'10px', marginBottom:'14px', flexWrap:'wrap'}}>
                    <div><label style={labelStyle}>From</label><input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={{...inputStyle, marginBottom:0, width:'150px'}}/></div>
                    <div><label style={labelStyle}>To</label><input type="date" value={to} onChange={e=>setTo(e.target.value)} style={{...inputStyle, marginBottom:0, width:'150px'}}/></div>
                </div>
                <div style={{display:'flex', gap:'10px'}}>
                    <button onClick={dlSales} style={{...btnStyle, marginTop:0, padding:'10px 18px'}}><Download size={14} style={{verticalAlign:'middle', marginRight:'6px'}}/>Sales Vouchers</button>
                    <button onClick={dlReceipts} style={{...btnStyle, marginTop:0, padding:'10px 18px', backgroundColor:theme.success}}><Download size={14} style={{verticalAlign:'middle', marginRight:'6px'}}/>Receipt Vouchers</button>
                </div>
            </div>

            <div style={{...cardStyle, backgroundColor:'#fffbeb', borderLeft:`4px solid ${theme.warning}`}}>
                <h4 style={{margin:'0 0 8px', color:'#92400e'}}>📋 Setup notes for Tally</h4>
                <ul style={{margin:0, paddingLeft:'20px', color:theme.navy, fontSize:'0.85rem', lineHeight:'1.7'}}>
                    <li>Patients become <strong>Sundry Debtor</strong> ledgers (auto-created on first import)</li>
                    <li>Sales A/c, Output CGST, Output SGST, Output IGST ledgers must exist</li>
                    <li>Receipt vouchers post against Cash A/c or Bank Account based on payment mode</li>
                    <li>Each bill becomes one Sales voucher; each Payment becomes one Receipt voucher</li>
                </ul>
            </div>
        </div>
    );
}

// ── QuickBooks ───────────────────────────────────────────────────────
function QbTab({ API_BASE }) {
    const [status, setStatus] = useState(null);
    const refresh = useCallback(async () => { try { const r = await axios.get(`${API_BASE}/integrations/quickbooks/status`); setStatus(r.data); } catch {} }, [API_BASE]);
    useEffect(()=>{refresh();}, [refresh]);

    const connect = async () => {
        try {
            const r = await axios.get(`${API_BASE}/integrations/quickbooks/authorize-url`);
            window.open(r.data.url, '_blank');
        } catch (e) { alert(e.response?.data?.error || 'Add Client ID under Configs first'); }
    };

    return (
        <div style={cardStyle}>
            <h3 style={{margin:'0 0 10px', color:theme.navy}}>QuickBooks Online</h3>
            {status?.connected ? (
                <div style={{padding:'12px', backgroundColor:'#f0fdf4', borderRadius:'8px', border:`1px solid #bbf7d0`, marginBottom:'14px'}}>
                    <div style={{fontWeight:'700', color:theme.success}}>✓ Connected</div>
                    <div style={{fontSize:'0.82rem', color:theme.textMuted, marginTop:'4px'}}>
                        Mode: {status.mode} · RealmId: <code>{status.realmId}</code> · Token expires {fmtDate(status.expiresAt)}
                    </div>
                    {status.lastSyncAt && <div style={{fontSize:'0.78rem', color:theme.textMuted}}>Last sync: {fmtDate(status.lastSyncAt)}</div>}
                </div>
            ) : (
                <div style={{padding:'12px', backgroundColor:'#fef3c7', borderRadius:'8px', marginBottom:'14px'}}>
                    <div style={{color:'#92400e', fontWeight:'600'}}>Not connected</div>
                </div>
            )}

            <ol style={{paddingLeft:'20px', color:theme.navy, fontSize:'0.86rem', lineHeight:'1.8'}}>
                <li>Register an app at <strong>developer.intuit.com</strong> and get OAuth Client ID + Secret</li>
                <li>Add Client ID + Secret under <strong>Configs → quickbooks</strong>, set mode to sandbox or live, enable</li>
                <li>Click <strong>Connect</strong> below — sign in to your QuickBooks company</li>
                <li>Bills + Payments will sync as Invoices + Payments to your QuickBooks chart of accounts</li>
            </ol>
            <button onClick={connect} style={{...btnStyle, marginTop:'12px'}}>{status?.connected ? 'Reconnect' : 'Connect'} QuickBooks</button>
        </div>
    );
}

function Modal({ title, onClose, children, wide }) {
    return (
        <div style={{position:'fixed', inset:0, backgroundColor:'rgba(15,23,42,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999}} onClick={onClose}>
            <div onClick={e=>e.stopPropagation()} style={{backgroundColor:'white', borderRadius:'14px', padding:'24px 28px', width:wide?'780px':'500px', maxWidth:'95vw', maxHeight:'92vh', overflow:'auto'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px'}}>
                    <h3 style={{margin:0, color:theme.navy}}>{title}</h3>
                    <button onClick={onClose} style={{background:'none', border:'none', cursor:'pointer', fontSize:'1.3rem'}}>×</button>
                </div>
                {children}
            </div>
        </div>
    );
}
